import { useEffect } from 'react';
import {
    domainEventBus,
    WindowLevelDeltaEvent,
    WindowLevelSetEvent,
} from '~/domain/events';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import { applyToWindowingTarget } from '~/engine/scene/windowingEnvironmentResolver';

/**
 * Handler for windowing domain events.
 * Subscribes to WindowLevelDeltaEvent and WindowLevelSetEvent
 * and updates the hosting environment's window level accordingly. A set
 * carries its source, so the image records a window the user chose; a step
 * does not (see Environment.adjustImageWindowLevel).
 *
 * Images are identified by their DICOM Series Instance UID (seriesUid).
 * Each event resolves against the dataset registry when it arrives, so an
 * Environment minted again since the last event is the one it reaches.
 */
export function useWindowingHandlers() {
    useEffect(() => {
        const handleDelta = (event: WindowLevelDeltaEvent) => {
            applyToWindowingTarget(
                getDatasetEnvironmentRegistry(),
                event.seriesUid,
                (env) =>
                    env.adjustImageWindowLevel(event.delta, event.seriesUid)
            );
        };

        const handleSet = (event: WindowLevelSetEvent) => {
            applyToWindowingTarget(
                getDatasetEnvironmentRegistry(),
                event.seriesUid,
                (env) =>
                    env.setImageWindowLevel(
                        event.windowLevel,
                        event.seriesUid,
                        event.source
                    )
            );
        };

        const subscriptions = [
            domainEventBus.subscribe(
                WindowLevelDeltaEvent.EVENT_TYPE,
                handleDelta
            ),
            domainEventBus.subscribe(WindowLevelSetEvent.EVENT_TYPE, handleSet),
        ];

        return () => {
            subscriptions.forEach((sub) => sub.unsubscribe());
        };
    }, []);
}
